import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Image from '../../../components/AppImage';
import { formatDate } from '../../../utils/dateUtils';

const ProjectPreview = ({ 
  formData, 
  activeLanguage, 
  isOpen = false, 
  onClose 
}) => {
  const getFieldValue = (fieldName) => {
    if (fieldName === 'title' || fieldName === 'description') {
      return formData?.[fieldName]?.[activeLanguage] || '';
    }
    return formData?.[fieldName] || '';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-success text-success-foreground';
      case 'ongoing': return 'bg-accent text-accent-foreground';
      case 'planned': return 'bg-warning text-warning-foreground';
      case 'paused': return 'bg-muted text-muted-foreground';
      case 'cancelled': return 'bg-error text-error-foreground';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'completed': return 'مكتمل';
      case 'ongoing': return 'جاري';
      case 'planned': return 'مخطط';
      case 'paused': return 'متوقف';
      case 'cancelled': return 'ملغي';
      default: return 'غير محدد';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'text-error';
      case 'high': return 'text-warning';
      case 'medium': return 'text-accent';
      case 'low': return 'text-muted-foreground';
      default: return 'text-muted-foreground';
    }
  };

  const getPriorityLabel = (priority) => {
    switch (priority) {
      case 'urgent': return 'عاجلة';
      case 'high': return 'عالية';
      case 'medium': return 'متوسطة';
      case 'low': return 'منخفضة';
      default: return 'غير محدد';
    }
  };

  const calculateDuration = () => {
    const startDate = new Date(getFieldValue('startDate'));
    const endDate = new Date(getFieldValue('endDate'));
    
    if (startDate && endDate && endDate > startDate) {
      const diffTime = Math.abs(endDate - startDate);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return `${diffDays} يوم`;
    }
    return 'غير محدد';
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-300 bg-background/80 backdrop-blur-sm animate-fade-in">
      <div className="flex items-start justify-center min-h-screen pt-8 px-4">
        <div className="w-full max-w-4xl bg-card border border-border rounded-lg shadow-elevation-3 animate-slide-up max-h-[90vh] overflow-y-auto">
          {/* Preview Header */}
          <div className="sticky top-0 bg-card border-b border-border px-6 py-4 rounded-t-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
                  <Icon name="Eye" size={20} color="white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-foreground font-arabic">
                    معاينة المشروع
                  </h2>
                  <p className="text-sm text-muted-foreground font-caption">
                    معاينة كيف سيظهر المشروع للمستخدمين
                  </p>
                </div>
              </div>
              
              <Button
                variant="ghost"
                size="icon"
                onClick={onClose}
                iconName="X"
                iconSize={20}
              />
            </div>
          </div>

          {/* Preview Content */}
          <div className="p-6">
            {/* Project Header */}
            <div className="mb-8">
              {getFieldValue('featuredImage') && (
                <div className="relative mb-6 rounded-lg overflow-hidden">
                  <Image
                    src={getFieldValue('featuredImage')}
                    alt={getFieldValue('featuredImageAlt')}
                    className="w-full h-64 object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                  
                  {/* Status Badge */}
                  <div className="absolute top-4 right-4">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(getFieldValue('status'))}`}>
                      {getStatusLabel(getFieldValue('status'))}
                    </span>
                  </div>
                </div>
              )}

              <div className="space-y-4">
                <h1 className="text-3xl font-bold text-foreground font-arabic" dir={activeLanguage === 'ar' ? 'rtl' : 'ltr'}>
                  {getFieldValue('title') || 'عنوان المشروع'}
                </h1>

                <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                  <div className="flex items-center space-x-2">
                    <Icon name="Calendar" size={16} />
                    <span>تاريخ البداية: {formatDate(getFieldValue('startDate'))}</span>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Icon name="Clock" size={16} />
                    <span>المدة: {calculateDuration()}</span>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Icon name="Users" size={16} />
                    <span>{getFieldValue('memberCount') || 0} عضو</span>
                  </div>
                  
                  <div className={`flex items-center space-x-2 ${getPriorityColor(getFieldValue('priority'))}`}>
                    <Icon name="Flag" size={16} />
                    <span>الأولوية: {getPriorityLabel(getFieldValue('priority'))}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Progress Section */}
            <div className="mb-8 bg-muted/50 rounded-lg p-6 border border-border">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-foreground font-arabic">
                  تقدم المشروع
                </h3>
                <span className="text-2xl font-bold text-primary">
                  {getFieldValue('progress') || 0}%
                </span>
              </div>
              
              <div className="w-full bg-background rounded-full h-3 mb-4">
                <div 
                  className="bg-primary h-3 rounded-full transition-all duration-300"
                  style={{ width: `${getFieldValue('progress') || 0}%` }}
                />
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">تاريخ البداية</p>
                  <p className="font-medium text-foreground">
                    {formatDate(getFieldValue('startDate'))}
                  </p>
                </div>
                
                <div>
                  <p className="text-muted-foreground">تاريخ النهاية</p>
                  <p className="font-medium text-foreground">
                    {formatDate(getFieldValue('endDate'))}
                  </p>
                </div>
                
                <div>
                  <p className="text-muted-foreground">الميزانية</p>
                  <p className="font-medium text-foreground">
                    {getFieldValue('budget') ? `${getFieldValue('budget')?.toLocaleString()} ل.س` : 'غير محدد'}
                  </p>
                </div>
                
                <div>
                  <p className="text-muted-foreground">المستخدم</p>
                  <p className="font-medium text-foreground">
                    {getFieldValue('usedBudget') ? `${getFieldValue('usedBudget')?.toLocaleString()} ل.س` : '0 ل.س'}
                  </p>
                </div>
              </div>
            </div>

            {/* Project Description */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-foreground font-arabic mb-4">
                وصف المشروع
              </h3>
              
              <div 
                className="prose prose-sm max-w-none text-foreground"
                dir={activeLanguage === 'ar' ? 'rtl' : 'ltr'}
              >
                {getFieldValue('description') ? (
                  <div className="whitespace-pre-wrap leading-relaxed">
                    {getFieldValue('description')}
                  </div>
                ) : (
                  <p className="text-muted-foreground italic">
                    لم يتم إضافة وصف للمشروع بعد
                  </p>
                )}
              </div>
            </div>

            {/* Project Tags */}
            {getFieldValue('tags') && (
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-foreground font-arabic mb-4">
                  العلامات
                </h3>
                
                <div className="flex flex-wrap gap-2">
                  {getFieldValue('tags')?.split(',')?.map((tag, index) => (
                    <span 
                      key={index}
                      className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm font-medium"
                    >
                      {tag?.trim()}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Project Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-card border border-border rounded-lg p-4">
                <h4 className="text-sm font-medium text-foreground mb-3 flex items-center space-x-2">
                  <Icon name="Info" size={16} />
                  <span>تفاصيل المشروع</span>
                </h4>
                
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">الفئة:</span>
                    <span className="text-foreground font-medium">
                      {getFieldValue('category') || 'غير محدد'}
                    </span>
                  </div>
                  
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">الحالة:</span>
                    <span className={`font-medium ${
                      getFieldValue('status') === 'completed' ? 'text-success' :
                      getFieldValue('status') === 'ongoing' ? 'text-accent' :
                      getFieldValue('status') === 'planned' ? 'text-warning' :
                      'text-muted-foreground'
                    }`}>
                      {getStatusLabel(getFieldValue('status'))}
                    </span>
                  </div>
                  
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">الأولوية:</span>
                    <span className={`font-medium ${getPriorityColor(getFieldValue('priority'))}`}>
                      {getPriorityLabel(getFieldValue('priority'))}
                    </span>
                  </div>
                </div>
              </div>

              <div className="bg-card border border-border rounded-lg p-4">
                <h4 className="text-sm font-medium text-foreground mb-3 flex items-center space-x-2">
                  <Icon name="TrendingUp" size={16} />
                  <span>الإحصائيات</span>
                </h4>
                
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">نسبة الإنجاز:</span>
                    <span className="text-primary font-bold">
                      {getFieldValue('progress') || 0}%
                    </span>
                  </div>
                  
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">عدد الأعضاء:</span>
                    <span className="text-foreground font-medium">
                      {getFieldValue('memberCount') || 0}
                    </span>
                  </div>
                  
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">المدة:</span>
                    <span className="text-foreground font-medium">
                      {calculateDuration()}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Preview Footer */}
          <div className="sticky bottom-0 bg-card border-t border-border px-6 py-4 rounded-b-lg">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                هذه معاينة لكيفية ظهور المشروع للمستخدمين
              </p>
              
              <div className="flex items-center space-x-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onClose}
                >
                  إغلاق المعاينة
                </Button>
                
                <Button
                  variant="default"
                  size="sm"
                  iconName="ExternalLink"
                  iconPosition="right"
                  iconSize={16}
                >
                  فتح في نافذة جديدة
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Backdrop */}
      <div 
        className="absolute inset-0 -z-10"
        onClick={onClose}
      />
    </div>
  );
};

export default ProjectPreview;